import { useState, useEffect, useCallback } from 'react';
import { staffService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialStaff } from '../data/staff';
import type { StaffMember } from '../types';

interface UseStaffReturn {
  staff: StaffMember[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (staff: Omit<StaffMember, 'id'>) => Promise<StaffMember>;
  update: (id: number | string, updates: Partial<StaffMember>) => Promise<StaffMember>;
  remove: (id: number | string) => Promise<void>;
  getByPin: (pin: string) => StaffMember | undefined;
  getByUsername: (username: string) => Promise<StaffMember | undefined>;
}

export const useStaff = (enabled = true): UseStaffReturn => {
  const dataSource = getDataSource();
  // Only initialize with initialStaff if using mock data, otherwise start empty
  const [staff, setStaff] = useState<StaffMember[]>(dataSource === 'mock' ? initialStaff : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStaff = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setStaff(initialStaff);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await staffService.getAll();
      setStaff(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load staff';
      setError(errorMessage);
      console.error('Error fetching staff:', err);
      // Don't fall back to initialStaff when using Firestore - keep existing staff or empty array
      // The real-time listener will update it when connection is restored
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchStaff();
      return;
    }

    setLoading(true);
    setError(null);
    let unsubscribe: (() => void) | null = null;

    const setupListener = async () => {
      try {
        const { staffService } = await import('../lib/database');
        if (staffService.subscribe) {
          unsubscribe = staffService.subscribe((updatedStaff) => {
            setStaff(updatedStaff);
            setLoading(false);
            setError(null);
          });
        }
      } catch (error) {
        console.error('❌ Error setting up staff listener:', error);
        setLoading(false);
      }
    };

    void setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [enabled, fetchStaff]);

  const create = useCallback(async (staffMember: Omit<StaffMember, 'id'>): Promise<StaffMember> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newStaff: StaffMember = {
        ...staffMember,
        id: Math.max(...staff.map(s => s.id), 0) + 1,
      };
      setStaff(prev => [...prev, newStaff]);
      return newStaff;
    }

    setError(null);
    try {
      // Check for duplicate authUserId if provided
      if (staffMember.authUserId) {
        const existing = await staffService.getByAuthUserId(staffMember.authUserId);
        if (existing.length > 0) {
          throw new Error(
            `A staff member with this Firebase Auth ID already exists: ${existing[0].name} (ID: ${existing[0].id}). Please update the existing record instead.`
          );
        }
      }
      
      const newStaff = await staffService.create(staffMember);
      // Don't optimistically update state when using Firestore - the real-time listener will handle it
      // This prevents duplicates when the listener fires
      // setStaff(prev => [...prev, newStaff]);
      return newStaff;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create staff member';
      setError(errorMessage);
      throw err;
    }
  }, [staff]);

  const update = useCallback(async (id: number | string, updates: Partial<StaffMember>): Promise<StaffMember> => {
    const dataSource = getDataSource();
    const isFirestoreId = typeof id === 'string';
    const matchStaff = (s: StaffMember & { firestoreId?: string }) =>
      isFirestoreId ? s.firestoreId === id : s.id === id;

    if (dataSource === 'mock') {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      const updatedStaff = staff.find(s => s.id === numericId);
      if (!updatedStaff) throw new Error('Staff member not found');
      const newStaff = { ...updatedStaff, ...updates };
      setStaff(prev => prev.map(s => s.id === numericId ? newStaff : s));
      return newStaff;
    }

    setError(null);
    try {
      const idString = String(id);
      const updatedStaff = await staffService.update(idString, updates);
      setStaff(prev => prev.map(s => (matchStaff(s as StaffMember & { firestoreId?: string }) ? updatedStaff : s)));
      return updatedStaff;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update staff member';
      setError(errorMessage);
      throw err;
    }
  }, [staff]);

  const remove = useCallback(async (id: number | string): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      setStaff(prev => prev.filter(s => s.id !== numericId));
      return;
    }

    setError(null);
    try {
      // Pass the ID as-is (can be firestoreId string or numeric ID)
      // The delete function will handle both cases
      await staffService.delete(id);
      
      // Update local state - filter by firestoreId if string, or by numeric id if number
      if (typeof id === 'string') {
        setStaff(prev => prev.filter(s => (s as any).firestoreId !== id));
      } else {
        setStaff(prev => prev.filter(s => s.id !== id));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete staff member';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getByPin = useCallback((pin: string): StaffMember | undefined => {
    return staff.find(s => s.pin === pin);
  }, [staff]);

  const getByUsername = useCallback(async (username: string): Promise<StaffMember | undefined> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      return staff.find(s => s.username?.toLowerCase() === username.toLowerCase());
    }

    try {
      const { staffService } = await import('../lib/database');
      const staffMember = await staffService.getByUsername(username);
      return staffMember as StaffMember | undefined;
    } catch (err) {
      console.error('Error fetching staff by username:', err);
      return undefined;
    }
  }, [staff]);

  return {
    staff,
    loading,
    error,
    refresh: fetchStaff,
    create,
    update,
    remove,
    getByPin,
    getByUsername,
  };
};

